import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Prompt, TableV2 } from '@anytag/shared-atoms';
import { useQueryHelper, useSelectItem, useToggle } from '@anytag/shared-hooks';
import {
  namedOperations,
  SocialAccountType,
  ProposalJobDescriptionBreakDownMode,
  useDeleteInfluencerPackageMutation
} from '@anytag/graphql';
import {
  BudgetBulkEditModal,
  ProposeInfluencersTableMode,
  ProposeInfluencersFormValues
} from '@anytag/shared-organisms';
import { usePackageProposalContext } from '../PackageProposalContext';
import { PackageActionBar } from './PackageActionBar';
import { ToggleJdModeAndBudgetEdit } from './ToggleJdModeAndBudgetEdit';
import {
  usePackageBulkEdit,
  usePackageInfluencersTable,
  useSocialAccountsForPackage,
  useInfluencerAccountForPackage,
  usePackageContentInfluencersSearch
} from './hooks';
import { RowPackageData } from './types';

export interface TemplateProps {
  packageId: number;
  data: RowPackageData[];
  loading: boolean;
}
type DeletingItem = {
  id: number;
  name: string;
  socialMedia: SocialAccountType;
};

export const Template = ({ loading, packageId, data }: TemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { on: openBulkModal, toggle: toggleBulkModal } = useToggle();
  const { on: isConfirmModalOpen, setOff: closeConfirmModal, setOn: openConfirmModal } = useToggle();
  const { resetSelectedItem } = useSelectItem<DeletingItem>();
  const {
    reset,
    formState: { isDirty }
  } = useFormContext<ProposeInfluencersFormValues>();
  const { jdMode, setFilter, isViewMode, isEditMode } = usePackageContentInfluencersSearch();
  const { proposalSummary, packageInfluencers, packageSocialAccounts, packageSocialAccountIds } =
    usePackageProposalContext();
  const {
    packageInfluencerIds,
    handleSelectSingleInfluencer,
    selectedInfluencerAccountIds,
    setSelectedInfluencerAccountIds,
    handleToggleSelectAllInfluencers
  } = useInfluencerAccountForPackage({ packageInfluencers });
  const {
    selectedSocialAccounts,
    selectedSocialAccountIds,
    setSelectedSocialAccountIds,
    handleSelectSingleAccount,
    handleToggleSelectAllAccounts
  } = useSocialAccountsForPackage({ packageSocialAccounts, packageSocialAccountIds });
  const { bulkLoading, handleInfluencersBudgetSubmit, handleSocialAccountBudgetSubmit } = usePackageBulkEdit({
    packageId,
    selectedSocialAccounts,
    selectedInfluencerAccountIds
  });
  const { callDeleteInfluencerPackage, loading: isDeleting } = useDeleteInfluencerPackageMutation({
    refetchQueries: [namedOperations.Query.PackageProposal]
  });

  useEffect(() => {
    if (isEditMode) {
      setSelectedSocialAccountIds([]);
    } else {
      setSelectedInfluencerAccountIds([]);
    }
  }, [isEditMode, packageInfluencers.length]);

  const isSocialMediaAccountsSelection =
    proposalSummary?.jdMode === ProposalJobDescriptionBreakDownMode.SOCIAL_BREAKDOWN && !isViewMode;
  const selectedAccountsIds = isSocialMediaAccountsSelection ? selectedSocialAccountIds : selectedInfluencerAccountIds;
  const selectedAccountsIdsLength = selectedAccountsIds.length;
  const hasAllSelectedIds = isSocialMediaAccountsSelection
    ? selectedAccountsIdsLength === packageSocialAccountIds.length
    : selectedAccountsIdsLength === packageInfluencerIds.length;

  const handleRowSelect = (influencerId: number, socialAccountId?: number) => {
    if (isSocialMediaAccountsSelection) {
      handleSelectSingleAccount(socialAccountId as number);
    } else {
      handleSelectSingleInfluencer(influencerId);
    }
  };
  const handleSelectAllIds = () => {
    if (isSocialMediaAccountsSelection) {
      setSelectedSocialAccountIds(packageSocialAccountIds);
    } else {
      setSelectedInfluencerAccountIds(packageInfluencerIds);
    }
  };
  const handleToggleAllIds = () => {
    if (isSocialMediaAccountsSelection) {
      handleToggleSelectAllAccounts();
    } else {
      handleToggleSelectAllInfluencers();
    }
  };

  const { columns, deleting } = usePackageInfluencersTable({
    currency: proposalSummary?.currency || '',
    onRowSelect: handleRowSelect,
    selectButtonProps: { onClick: handleSelectAllIds },
    listSelectedIds: isSocialMediaAccountsSelection ? selectedSocialAccountIds : selectedInfluencerAccountIds,
    resetSelectedItem,
    packageId
  });

  const handleRemoveInfluencers = async (callback?: () => void) => {
    try {
      await callDeleteInfluencerPackage({ variables: { influencerIds: selectedAccountsIds, packageId } });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    } finally {
      callback?.();
    }
  };

  const handleBulkEditSuccess = () => {
    setFilter({ tableViewMode: ProposeInfluencersTableMode.VIEW });
    reset();
  };

  return (
    <>
      <ToggleJdModeAndBudgetEdit onShowConfirmModal={openConfirmModal} />

      <div css={{ position: 'relative' }}>
        {selectedAccountsIdsLength > 0 ? (
          <PackageActionBar
            isRemoving={isDeleting}
            packageId={proposalSummary?.id}
            isViewMode={isViewMode}
            onSelectAll={handleSelectAllIds}
            onRemove={handleRemoveInfluencers}
            hasAllSelected={hasAllSelectedIds}
            onToggleSelect={handleToggleAllIds}
            onToggleBulkEditModal={toggleBulkModal}
            numberSelected={selectedAccountsIdsLength}
            selectedSocialAccounts={selectedSocialAccounts}
            selectedInfluencerAccountIds={selectedInfluencerAccountIds}
            isSocialMediaAccountsSelection={isSocialMediaAccountsSelection}
          />
        ) : null}
        <TableV2
          loading={loading}
          data={data}
          columns={columns}
          rowHeight="62px"
          className="proposal-table"
          empty={{
            description: t('Please add your social accounts'),
            title: t('No social accounts have been added to the package'),
            linkProps: {
              href: '/package/$id/content/search',
              params: { id: packageId.toString() },
              label: t('in the select influencers tab')
            }
          }}
          noTopBorderRadius
          hasBorder
          hasLoadingOverlay={deleting}
        />
      </div>
      <BudgetBulkEditModal
        onSuccess={handleBulkEditSuccess}
        open={openBulkModal}
        onClose={toggleBulkModal}
        currency={proposalSummary?.currency || ''}
        jdMode={jdMode as ProposalJobDescriptionBreakDownMode}
        loading={bulkLoading}
        onInfluencersBudgetSubmit={handleInfluencersBudgetSubmit}
        onSocialAccountBudgetSubmit={handleSocialAccountBudgetSubmit}
      />

      <Prompt
        size="lg"
        when={isDirty}
        open={isConfirmModalOpen}
        title={t('Dialog.Save your changes')}
        description={t('leaveWithoutSaveWarningText')}
        footer={({ reset, proceed }) => ({
          cancelButton: {
            title: t('Button.Leave without saving'),
            onClick: () => {
              setFilter({ tableViewMode: ProposeInfluencersTableMode.VIEW });
              closeConfirmModal();
              setTimeout(() => {
                proceed?.();
              }, 0);
            },
            submitButton: {
              title: t('Button.Keep editing'),
              onClick: () => {
                closeConfirmModal();
                reset?.();
              }
            }
          }
        })}
      />
    </>
  );
};
